/**
 * Vuex module for call queues.
 */
import debug from 'debug'
import Queue from 'Store/class/queue'
import events from 'Store/events'
import * as RMQClient from '@/rmqclient'
import API from '@/api'

const dbg = debug('store:action:queue:')

const actions = {
  init: function ({ commit, state }) {
    dbg('Init application: queues and agents')
    API.getQueues(0, state.pagination.perPage).then(resp => {
      commit('RESET')
      resp.data.queues.forEach(queue => {
        commit('ADD_NEW', queue)
      })
      RMQClient.sendCmd({ action: 'CoreShowChannels', amictx: 'queue' })
      commit('INIT_PAGINATION', resp.data.total)
      this.dispatch('queues/loadWatched')
    })
    this.dispatch('queues/allQueueNames')
  },

  loadWatched: function ({ commit, state }) {
    API.getAllWatchedQueues().then(resp => {
      commit('SET_WATCHED', resp.data)
      this.dispatch('queues/queueStatus')
    })
  },

  allQueueNames: function ({ commit, state }) {
    API.getAllQueueNames().then(resp => {
      commit('SET_ALL_QUEUE_NAMES', resp.data?.queues)
    })
  },

  queueStatus: function ({ commit, state }) {
    var queueIds = state.queues.map(queue => queue.id)
    var watchedQueueIds = state.watchedQueues.map(queue => queue.id)
    var allQueueIds = new Set([...queueIds, ...watchedQueueIds])
    allQueueIds.forEach(function (queueId) {
      RMQClient.sendCmd({ action: 'QueueStatus', queue: queueId })
    })
  },

  pageLoad: function ({ commit, state }, page) {
    return new Promise((resolve, reject) => {
      const start = (page - 1) * state.pagination.perPage
      return API.getQueues(start, state.pagination.perPage).then(resp => {
        commit('RESET')
        resp.data.queues.forEach(queue => {
          commit('ADD_NEW', queue)
        })
        commit('SET_PAGE', { page: page, start: start })
        this.dispatch('queues/queueStatus')
        resolve()
      }).catch(err => {
        reject(err.response.data)
      })
    })
  },

  toggleWatch: function ({ commit }, queue) {
    commit('QUEUE_WATCH_TOGGLE', queue)
  },

  setNavMini: function ({ commit }, val) {
    commit('SET_NAV_MINI', val)
  },

  showAgentsPane: function ({ commit }, val) {
    commit('SET_AGENTS_PANE_SHOW', !!val) // assure boolean value
  },

  setActive: function ({ commit }, queue) {
    commit('SET_ACTIVE_QUEUE', queue) // assure boolean value
  },

  loadQueueSummary: function ({ commit }, params) {
    return API.getQueueSummaryReport(params.queues, params.startDate, params.endDate, params.timezone, params.hourly).then(response => {
      commit('SET_QUEUE_SUMMARY_RESULTS', response.data)
    })
  },

  loadQueueCallDetails: function ({ commit }, params) {
    return API.getQueueCallDetailsReport(params.queues, params.startDate, params.endDate, params.timezone, params.limit, params.offset).then(response => {
      commit('SET_QUEUE_CALL_DETAILS_RESULTS', response.data)
    })
  },

  loadAgentDetails: function ({ commit }, params) {
    return API.getQueueAgentDetails(params.agentExtension, params.startDate, params.endDate, params.timezone, params.limit, params.offset).then(response => {
      commit('SET_AGENT_DETAILS_RESULTS', response.data)
    })
  },

  loadReportType: function ({ commit }, params) {
    const { reportType, queues, agentExtension, startDate, endDate, timezone, hourly, limit, offset } = params
    switch (reportType) {
      case 'summary':
        return this.dispatch('queues/loadQueueSummary', { queues, startDate, endDate, timezone, hourly })
      case 'callDetails':
        return this.dispatch('queues/loadQueueCallDetails', { queues, startDate, endDate, timezone, limit, offset })
      case 'agentDetails':
        return this.dispatch('queues/loadAgentDetails', { agentExtension, startDate, endDate, timezone, limit, offset })
      default:
        return Promise.reject(new Error('Unknown report type'))
    }
  }
}

const mutations = {
  ROUTE_ACTIONS (state, data) {
    const watchedQueue = Queue.find(state.watchedQueues, data.queue)
    const drawerQueue = Queue.find(state.queues, data.queue)
    const queues = [watchedQueue, drawerQueue]
    queues.forEach((queue) => {
      if (!queue) return // queue is not here
      switch (data.event) {
        case 'QueueCallerJoin':
          queue.callerJoin(data)
          break
        case 'AgentCalled':
          queue.agentCalled(data)
          break
        case 'AgentConnect':
          queue.agentConnect(data)
          break
        case 'AgentComplete':
          queue.agentComplete(data)
          break
        case 'QueueCallerLeave':
          queue.callerLeave(data)
          break
        case 'QueueMemberStatus':
          queue.memberStatus(data)
          break
        case 'QueueCallerAbandon':
          queue.callerAbandon(data)
          break
        case 'QueueMemberPause':
          queue.memberPause(data)
          break
        case 'QueueParams':
          queue.queueParams(data)
          break
        case 'QueueMember':
          queue.queueMember(data)
          break
        case 'QueueEntry':
          queue.queueEntry(data)
          break
        default:
          dbg(`Do not know how to route event "${data.event}"`)
          break
      }
    })
  },

  ADD_NEW (state, data) {
    const queue = new Queue(data)
    state.queues.push(queue)
  },

  SET_WATCHED (state, data) {
    state.watchedQueues = data.map(queue => {
      return new Queue(queue)
    })
  },

  INIT_PAGINATION (state, total) {
    state.pagination.total = total
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
    state.pagination.curPage = 1
    state.pagination.start = 0
    state.pagination.limit = state.pagination.perPage
  },

  CHAN_ADD (state, data) {
    try {
      Queue.callFromQueueChan(state.queues, data)
      Queue.callFromQueueChan(state.watchedQueues, data)
    } catch (e) {
      dbg(`CHANADD error: ${e.message}`)
    }
  },

  QUEUE_WATCH_TOGGLE (_, queue) {
    const watch = !queue.watch
    queue.watch = watch
    const watchIndex = state.watchedQueues.findIndex(watchedQueue => watchedQueue.id === queue.id)
    if (watch) {
      state.watchedQueues.push(queue)
    } else {
      state.watchedQueues.splice(watchIndex, 1)
    }
    API.queueWatchToggle(queue).catch(err => {
      dbg(err.response)
      events.pubError('Failed to toggle watch queue!')
    })
  },

  SET_NAV_MINI (state, val) {
    state.navMini = val
  },

  SET_AGENTS_PANE_SHOW (state, val) {
    state.agentsPaneShow = val
  },

  SET_ACTIVE_QUEUE (state, queue) {
    state.activeQueue = queue
  },

  SET_PAGE (state, { page, start }) {
    state.pagination.curPage = page
    state.pagination.start = start
  },

  SET_QUEUE_SUMMARY_RESULTS (state, results) {
    state.queueSummaryResults = results
  },

  SET_QUEUE_CALL_DETAILS_RESULTS (state, results) {
    state.queueCallDetailsResults = results
  },

  SET_AGENT_DETAILS_RESULTS (state, results) {
    state.agentDetailsResults = results
  },

  SET_ALL_QUEUE_NAMES (state, names) {
    state.allQueueNames = names
  },

  RESET (state) {
    state.queues.splice(0)
  }
}

const state = {
  queues: [],
  allQueueNames: [],
  activeQueue: null,
  watchedQueues: [],
  navMini: true,
  agentsPaneShow: false,
  pagination: {
    perPage: 10,
    pages: 0,
    curPage: 0,
    start: 0,
    total: 0
  },
  queueSummaryResults: {},
  queueCallDetailsResults: {},
  agentDetailsResults: {}
}

const getters = {
  queues: state => state.queues,
  allQueueNames: state => state.allQueueNames,
  getNavMini: state => state.navMini,
  watchedQueues: state => state.watchedQueues,
  totalActiveCalls: state => Queue.totalActiveCalls(state.watchedQueues),
  totalCallWaiting: state => Queue.totalCallWaiting(state.watchedQueues),
  totalCompletedCalls: state => Queue.totalCompletedCalls(state.watchedQueues),
  totalAbandonedCalls: state => Queue.totalAbandonedCalls(state.watchedQueues),
  totalAgents: state => Queue.totalAgents(state.watchedQueues),
  totalAgentsPaused: state => Queue.totalAgentsPaused(state.watchedQueues),
  totalAgentsUnpaused: state => Queue.totalAgents(state.watchedQueues) - Queue.totalAgentsPaused(state.watchedQueues),
  agentsPaneShow: state => state.agentsPaneShow,
  pagination: state => state.pagination,
  active: state => state.activeQueue,
  queueSummary: state => state.queueSummaryResults,
  callDetails: state => state.queueCallDetailsResults,
  agentDetails: state => state.agentDetailsResults
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
